import { PORTAL_CLASS_NAME } from '@billy/design-system'
import { Button, Icon, LayoutHeader, Stepper } from '@shinetools/lumen-react'
import { X } from 'lucide-react'
import { ReactElement, ReactNode, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useFecImportModal } from './hooks/useFecImportModal'
import * as Styled from './styles'

interface FecImportModalStep {
  label: string
}

interface FecImportModalProps {
  children?: ReactNode
  currentStep: number
  isContentCentered?: boolean
  onClose: () => void
  showStepper?: boolean
  steps: FecImportModalStep[]
  title: string
}

export const FecImportModal = ({
  children,
  currentStep,
  isContentCentered = true,
  onClose,
  showStepper = true,
  steps,
  title,
}: FecImportModalProps): ReactElement => {
  const { t } = useTranslation()
  const overlayRef = useRef<HTMLDivElement>(null)
  const headerCloseLabel = `${t('close')} (${title})`

  useFecImportModal({ onClose, overlayRef })

  const handleCloseButtonClick = useCallback(() => {
    onClose()
  }, [onClose])

  return createPortal(
    <Styled.FecImportModalOverlay
      aria-label={title}
      aria-modal="true"
      className={PORTAL_CLASS_NAME}
      ref={overlayRef}
      role="dialog"
      tabIndex={-1}
    >
      <LayoutHeader
        actions={
          <Button
            aria-label={headerCloseLabel}
            icon={<Icon icon={X} />}
            onClick={handleCloseButtonClick}
            size="large"
            type="button"
            variant="text"
          />
        }
        center={
          showStepper ? (
            <Styled.FecImportModalStepper>
              <Stepper activeStep={currentStep - 1} steps={steps} />
            </Styled.FecImportModalStepper>
          ) : undefined
        }
        title={title}
      />
      <Styled.FecImportModalBody $isContentCentered={isContentCentered}>{children}</Styled.FecImportModalBody>
    </Styled.FecImportModalOverlay>,
    document.body,
  )
}
